/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/ProfilePage.tsx
 * PURPOSE: Employee profile page — view personal info, branch, role
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-08-17
 * COMPLIANCE: SDLC 6.5.0 + Sprint Governance Rule 3 (Zero-Mock Tolerance)
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance
 *
 * @business-context
 * - Domain: Employee Self-Service, Profile View
 * - Impact: Employee can view their own info
 * - Stakeholders: Employees (71)
 *
 * @technical-architecture
 * - Layer: Presentation Layer
 * - Pattern: Read-only profile display
 * - Dependencies: React 18, useAuthStore
 *
 * @cross-references
 * - ISS-EMP-11 (2026-08-16): this route (unlike Dashboard/Attendance/Schedule/
 *   Leave/Utilities/AttendanceHistory) had no EmployeeBottomNav — the only way
 *   out was the "← Quay lại" button (navigate(-1), history-dependent). Wired in
 *   the same shared component the other 6 employee routes use; added
 *   paddingBottom: var(--nqh-space-20) to the root inline-style wrapper (this
 *   page has no CSS module) so the fixed nav doesn't cover the bottom content,
 *   matching the .page padding-bottom convention used elsewhere.
 * - 2026-09-04 (structural-layout + button-proportion parity vs mobile source
 *   mobile/src/screens/ProfileScreen.tsx, same audit already applied to the
 *   sibling AttendanceModern.tsx/ScheduleModern.tsx this session; this file
 *   has no ProfilePage.module.css — confirmed by directory listing — so
 *   fixes below stay in the file's existing inline-style approach, using
 *   `var(--nqh-*)` tokens where the sibling .module.css files establish a
 *   matching convention):
 *   (1) No `<header>`/`<main>` landmark split — every other EmployeeBottomNav
 *       sibling (AttendanceModern/ScheduleModern/Utilities) renders
 *       `<header><div headerContent><h1 id=".."/></div></header>` +
 *       `<main aria-labelledby="..">`; this file rendered one flat `<div>`
 *       with no landmarks and no `h1`/`main` id-linking. Added, header
 *       chrome (background/border-bottom/sticky) copied verbatim from
 *       ScheduleModern.module.css/Utilities.module.css `.header`/
 *       `.headerContent` (identical values in both).
 *   (2) Header action button was "← Quay lại" → `navigate(-1)` — the
 *       ISS-EMP-11 note above already flags this as history-dependent, i.e.
 *       exactly the fragility that motivated adding EmployeeBottomNav in the
 *       first place. Every sibling page instead uses a fixed-route
 *       "Về Dashboard" button (ScheduleModern.tsx:259-263,
 *       Utilities.tsx:166-169) — mobile's ProfileScreen has no header action
 *       button at all (tab-root, no back-stack), which is consistent with
 *       retiring the history-dependent nav; matched the WEB sibling
 *       convention (fixed route, not bare) since that convention is this
 *       session's proven template. Target `/dashboard/staff`, matching
 *       Utilities.tsx (also an employee-tier EmployeeBottomNav sibling).
 *       Button chrome (padding/bg/border/radius/color) copied verbatim from
 *       ScheduleModern.module.css:88-98 `.backButton`.
 *   (3) Wrapping/grouping: mobile Card-wraps the whole avatar+name+role block
 *       (ProfileScreen.tsx:100-124 `<Card style={styles.profileCard}>`); web
 *       rendered it as a bare, unstyled `<div>` while the info-fields block
 *       immediately below it WAS card-styled (white/border/radius) — an
 *       inconsistent wrap/no-wrap split with no mobile analogue. Gave the
 *       avatar block the same card treatment already established one block
 *       down in this same file (self-consistent, no new token introduced).
 *   (4) Spacing: one-off `marginBottom: 24`/`marginTop: 24` inline hacks
 *       between top-level sections replaced with a single `gap:
 *       var(--nqh-space-4)` on a flex-column `<main>` — same root-cause-#3
 *       conversion already applied to `.main` in AttendanceModern.module.css
 *       and ScheduleModern.module.css.
 *   (5) Button proportions on the one real action button in `<main>` (logout,
 *       excluding the header nav button covered by (2)): was
 *       `borderRadius: 12` (the CARD radius, not a button radius — the exact
 *       same root-cause bug already found and fixed on
 *       AttendanceModern.module.css's primaryButton/secondaryButton, see that
 *       file's 2026-09-04 header entry) and no explicit `minHeight`. Mobile's
 *       Đăng xuất button is `<AppButton variant="danger">`
 *       (mobile/src/components/ui/AppButton.tsx:73-75:
 *       `minHeight:48, borderRadius:brandRadius.md(8)`). Fixed to
 *       `minHeight: 48` + `borderRadius: 8` (raw — no `--nqh-radius-*` token
 *       currently equals 8px, same carve-out documented in
 *       AttendanceModern.module.css's file header). Background color
 *       `#ef4444` left as-is but noted: it is byte-identical to
 *       `--nqh-chili-500` (AppButton's danger-variant color,
 *       `b.chili[500]`) — swapped to the token for hygiene, zero visual
 *       change. Width `100%` was already correct (matches mobile: AppButton
 *       stretches to its Card's full width when it is the sole child).
 *   Reviewed, kept as intentional (NOT changed):
 *     - Info-fields table (Email/Họ tên/Vai trò/Trạng thái/Mã nhân viên) has
 *       no direct mobile equivalent (mobile shows name/role/email/employeeId
 *       inline in the profile card, not as a separate key-value table) and
 *       re-states name/role already shown above it. Read as a deliberate,
 *       more information-dense web adaptation (common profile-page pattern:
 *       prominent identity block + scannable details table), not accidental
 *       duplication — left in place.
 *     - (SUPERSEDED by the 2026-09-04 (cont.) entry below — the two RowLink
 *       menu groups this note originally deferred are now built.)
 * - 2026-09-04 (cont.) — added the "Bảo mật"/"Cá nhân" RowLink-equivalent menu
 *   groups the note above had deferred (ProfileScreen.tsx:126-173, `RowLink`
 *   defined at mobile/src/components/ui/RowLink.tsx: row minHeight 44,
 *   leading icon 20px, label 14px, border-bottom between rows except the
 *   last, trailing chevron). Re-verified the route cross-check against
 *   current `frontend/src/App.tsx` (routes may have moved since the deferred
 *   note was written) — still only 2 of 6 mobile targets have a real web
 *   route:
 *     - "Đổi mật khẩu" → `/change-password` (App.tsx:703) — LIVE, wired.
 *     - "Lịch sử chấm công" → `/attendance/history` (App.tsx:669) — LIVE,
 *       wired (web has no equivalent of mobile's deep-link-into-Attendance-
 *       tab-history-state; the dedicated history route is the web analogue).
 *     - "Nghỉ phép" → `/leave-requests` (App.tsx:647) — LIVE, wired (web has
 *       no `readOnly` param equivalent; navigates to the same page mobile's
 *       Utilities tile and Dashboard both already use for this action).
 *     - "Cài đặt bảo mật thiết bị" (SecuritySettings), "Thông báo"
 *       (Notifications-as-a-page), "Cài đặt" (AppSettings) — grepped
 *       App.tsx again, confirmed NO web route exists for any of the three
 *       (only unrelated `/tenant-admin/settings` and `/tenant-admin/security`
 *       exist, a different persona/surface). Zero-Mock Tolerance: not
 *       dead-linked. Neither group would become empty by omitting these
 *       (Bảo mật keeps 1 live row, Cá nhân keeps 2), so per the task's own
 *       guidance the groups stay and these 3 rows render using the same
 *       locked/"Sắp có" convention already established in
 *       `Utilities.module.css` (`.tileLocked`/`.tileBadge`: muted text,
 *       non-interactive, small turmeric badge) — adapted from Utilities'
 *       grid-tile shape to RowLink's row shape (badge replaces the chevron
 *       instead of sitting in a tile corner), not a new pattern.
 *   Mobile's 3rd `<Card style={styles.groupCard}>` (ProfileScreen.tsx:176-178)
 *   contains only the "Đăng xuất" `AppButton` — no other content — so it is
 *   the same logout action already on this page, just Card-wrapped on mobile
 *   and previously bare here. Gave the logout button the same
 *   white/border/radius-12/padding-16 card wrapper the avatar and menu-group
 *   cards on this page use, matching mobile's wrap; the button itself
 *   (minHeight 48 / borderRadius 8, note (5) above) is unchanged.
 *     - `maxWidth: 480` on the root wrapper already matches
 *       AttendanceModern.module.css's newest 480px app-shell width (its
 *       2026-09-04 pixel-mockup pass) rather than ScheduleModern/Utilities'
 *       older 1400px dashboard-width convention — left as-is (already
 *       ahead, not behind).
 * - Related: docs/05-test/13-test-issues/RELEASE-V0-ISSUES-CONSOLIDATED-2026-08-09.md §45g
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import { KeyRound, ShieldCheck, Clock, Calendar, Bell, Settings, ChevronRight } from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { EmployeeBottomNav } from '../components/navigation/EmployeeBottomNav';

// Mirrors mobile/src/screens/ProfileScreen.tsx:126-173 ("Bảo mật" + "Cá nhân"
// RowLink groups). `path: null` + `locked: true` = no web route exists yet
// (Zero-Mock Tolerance — see file header 2026-09-04 (cont.) note for the
// per-row route audit); rendered via the Utilities.module.css locked-tile
// convention adapted to a row shape instead of a dead link.
interface ProfileMenuRow {
  key: string;
  label: string;
  icon: LucideIcon;
  path: string | null;
  locked: boolean;
}

interface ProfileMenuGroup {
  title: string;
  rows: ProfileMenuRow[];
}

const PROFILE_MENU_GROUPS: ProfileMenuGroup[] = [
  {
    title: 'Bảo mật',
    rows: [
      { key: 'change-password', label: 'Đổi mật khẩu', icon: KeyRound, path: '/change-password', locked: false },
      { key: 'security-settings', label: 'Cài đặt bảo mật thiết bị', icon: ShieldCheck, path: null, locked: true },
    ],
  },
  {
    title: 'Cá nhân',
    rows: [
      { key: 'attendance-history', label: 'Lịch sử chấm công', icon: Clock, path: '/attendance/history', locked: false },
      { key: 'leave-requests', label: 'Nghỉ phép', icon: Calendar, path: '/leave-requests', locked: false },
      { key: 'notifications', label: 'Thông báo', icon: Bell, path: null, locked: true },
      { key: 'app-settings', label: 'Cài đặt', icon: Settings, path: null, locked: true },
    ],
  },
];

const ROLE_LABELS: Record<string, string> = {
  TENANT_OWNER: 'Chủ doanh nghiệp',
  TENANT_ADMIN: 'Quản trị viên',
  // CORPORATE_MANAGER (2026-08-28, @ba): thiếu hẳn — fallback `|| user?.role`
  // (dòng dưới) sẽ lộ enum thô "CORPORATE_MANAGER" ra màn hình, phạm
  // UI-LABEL-RULE-01. Bổ sung, khớp UI-LABELS-VI-ENUM.md §5 (CHỐT 2026-08-28).
  CORPORATE_MANAGER: 'Quản lý phòng ban',
  BRANCH_MANAGER: 'Quản lý chi nhánh',
  SHIFT_MANAGER: 'Quản lý ca',
  WAITER: 'Nhân viên phục vụ',
  CASHIER: 'Thu ngân',
  CHEF: 'Đầu bếp',
  HEAD_CHEF: 'Bếp trưởng',
  KITCHEN_STAFF: 'Nhân viên bếp',
  CLEANER: 'Nhân viên vệ sinh',
  DELIVERY_STAFF: 'Nhân viên giao hàng',
  TRAINEE: 'Thực tập sinh',
  EMPLOYEE: 'Nhân viên',
};

export const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div style={{ maxWidth: 480, margin: '0 auto', paddingBottom: 'var(--nqh-space-20)' }}>
      <header style={{
        background: 'white',
        borderBottom: '1px solid var(--nqh-gray-200)',
        padding: 'var(--nqh-space-4) 0',
        position: 'sticky',
        top: 0,
        zIndex: 50, // --nqh-z-50 token value (raw: CSS custom props aren't valid TS zIndex types)
      }}>
        <div style={{
          padding: '0 var(--nqh-space-4)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <h1 id="profile-page-title" style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>Thông tin cá nhân</h1>
          <button
            onClick={() => navigate('/dashboard/staff')}
            style={{
              padding: 'var(--nqh-space-2) var(--nqh-space-4)',
              background: 'var(--nqh-gray-100)',
              border: '1px solid var(--nqh-gray-300)',
              borderRadius: 12, // var(--nqh-radius-lg), matches ScheduleModern/Utilities .backButton
              color: 'var(--nqh-gray-700)',
              cursor: 'pointer',
              fontSize: 14,
            }}
          >
            Về Dashboard
          </button>
        </div>
      </header>

      <main
        aria-labelledby="profile-page-title"
        style={{
          padding: 'var(--nqh-space-4)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--nqh-space-4)',
        }}
      >
        {/* Avatar — card-wrapped to match mobile's single profileCard
            (ProfileScreen.tsx:100-124), see file header note (3) */}
        <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', padding: 16, textAlign: 'center' }}>
          <div style={{
            width: 80, height: 80, borderRadius: '50%', background: '#e5e7eb',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto', fontSize: 32,
          }}>
            👤
          </div>
          <h2 style={{ fontSize: 18, fontWeight: 600, marginTop: 12 }}>
            {user?.full_name || 'Nhân viên'}
          </h2>
          <p style={{ color: '#6b7280', fontSize: 14 }}>
            {ROLE_LABELS[user?.role || ''] || user?.role || ''}
          </p>
        </div>

        {/* Info fields */}
        <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          {([
            { label: 'Email', value: user?.email },
            { label: 'Họ tên', value: user?.full_name },
            { label: 'Vai trò', value: ROLE_LABELS[user?.role || ''] || user?.role },
            { label: 'Trạng thái', value: user?.is_active ? 'Đang hoạt động' : 'Không hoạt động' },
            // ISS-PILOT-12 (2026-08-09): đọc employee_code thật (vd "NQDL00008") —
            // trước đây đọc nhầm user.id (UUID nội bộ, không có ý nghĩa nghiệp vụ).
            // Có thể trống nếu TA chưa gán lúc tạo (field optional) → "—" fallback.
            { label: 'Mã nhân viên', value: user?.employee_code || '' },
          ] as const).map((field, i) => (
            <div key={i} style={{
              display: 'flex', justifyContent: 'space-between', padding: '12px 16px',
              borderBottom: i < 4 ? '1px solid #f3f4f6' : 'none',
            }}>
              <span style={{ color: '#6b7280', fontSize: 14 }}>{field.label}</span>
              <span style={{ fontSize: 14, fontWeight: 500 }}>{field.value || '—'}</span>
            </div>
          ))}
        </div>

        {/* "Bảo mật" / "Cá nhân" menu groups — structural parity with mobile's
            two RowLink Card groups, see file header 2026-09-04 (cont.) note.
            Card chrome (white/border/radius-12/padding-16) matches Card.tsx
            (borderWidth 1, brandRadius.lg=12, padding spacing.lg=16); group
            title matches CardHeader's title style (fontSize.lg=18/600). */}
        {PROFILE_MENU_GROUPS.map((group) => (
          <div
            key={group.title}
            style={{ background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', padding: 16 }}
          >
            <h3 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 12px', color: '#111827' }}>
              {group.title}
            </h3>
            {group.rows.map((row, i) => {
              const Icon = row.icon;
              const isLast = i === group.rows.length - 1;
              const rowBorder = isLast ? 'none' : '1px solid #f3f4f6';

              if (row.locked) {
                return (
                  <div
                    key={row.key}
                    aria-disabled="true"
                    style={{
                      display: 'flex', alignItems: 'center', minHeight: 44,
                      padding: '8px 0', gap: 8, borderBottom: rowBorder,
                      color: '#9ca3af', cursor: 'not-allowed',
                    }}
                  >
                    <Icon size={20} aria-hidden="true" />
                    <span style={{ flex: 1, fontSize: 14 }}>{row.label}</span>
                    {/* "Sắp có" badge — same locked convention as
                        Utilities.module.css .tileBadge (turmeric text on
                        turmeric-50 bg), adapted from tile-corner to row-end. */}
                    <span style={{
                      fontSize: 10, color: 'var(--nqh-turmeric-500)',
                      background: 'var(--nqh-turmeric-50, #fffbeb)',
                      padding: '2px 6px', borderRadius: 'var(--nqh-radius-full)',
                    }}>
                      Sắp có
                    </span>
                  </div>
                );
              }

              return (
                <button
                  key={row.key}
                  type="button"
                  onClick={() => navigate(row.path as string)}
                  style={{
                    display: 'flex', alignItems: 'center', width: '100%',
                    minHeight: 44, padding: '8px 0', gap: 8,
                    background: 'transparent', border: 'none',
                    borderBottom: rowBorder, cursor: 'pointer', textAlign: 'left',
                  }}
                >
                  <Icon size={20} color="#374151" aria-hidden="true" />
                  <span style={{ flex: 1, fontSize: 14, color: '#111827' }}>{row.label}</span>
                  <ChevronRight size={18} color="#9ca3af" aria-hidden="true" />
                </button>
              );
            })}
          </div>
        ))}

        {/* Logout — Card-wrapped to match mobile's 3rd groupCard
            (ProfileScreen.tsx:176-178, which contains only this button), see
            file header 2026-09-04 (cont.) note. Button-proportion parity vs
            mobile's <AppButton variant="danger"> (AppButton.tsx:73-75:
            minHeight 48, borderRadius brandRadius.md=8), see file header
            note (5). */}
        <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', padding: 16 }}>
          <button
            onClick={handleLogout}
            style={{
              width: '100%',
              minHeight: 48,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '0 16px',
              background: 'var(--nqh-chili-500)', // byte-identical to prior raw #ef4444
              color: 'white', border: 'none',
              borderRadius: 8, // AppButton brandRadius.md — raw, no --nqh-radius-* token equals 8px
              fontSize: 16, fontWeight: 600, cursor: 'pointer',
            }}
          >
            Đăng xuất
          </button>
        </div>
      </main>

      <EmployeeBottomNav />
    </div>
  );
};

export default ProfilePage;
